/**
 * Pure numbers + layout helpers of the grouped lists (KAN-1016) — no theme imports, so `utils/`
 * and `ui/` can read them without pulling components in. `components/common/groupedList/
 * groupedListTokens.js` re-exports these: ONE source of the numbers.
 */
export const GROUPED_GEOMETRY = {
  checkSlot: { web: { box: 18, gap: 12 }, touch: { box: 20, gap: 10 } },
  edgePx: { web: 20, tablet: 16, phone: 14 },
  /**
   * THE touch row standard (founder, 2026-10-26) — read off Catálogo's phone row in select mode:
   * card inner edge (`edgePx`) → 20px box → 10px gap → content, 8px top/bottom, 12px right, 4px
   * between line 1 and line 2. Select mode OFF uses the same padding (the slot is just closed).
   */
  touchRow: { padYPx: 8, padRightPx: 12, gapPx: 10, lineGapPx: 4, minHeightPx: 58 },
};

/**
 * Padding of a touch list row at a given edge. The right side defaults to the SAME edge as the customer row
 * above, so a record's 44px trailing slot (⋮ / chevron) and its amount end on the customer's right edge;
 * Catálogo (no customer row) passes the 12px reference. — identical with select mode off, on, and selected. */
export function groupedTouchRowPadding(edge, padRight = edge) {
  const r = GROUPED_GEOMETRY.touchRow;
  return `${r.padYPx}px ${padRight}px ${r.padYPx}px ${edge}px`;
}

/** Height-affecting box of a touch list row: padding + min-height + border-box. State-independent. */
export function groupedTouchRowBox(edge, padRight = edge) {
  return { padding: groupedTouchRowPadding(edge, padRight), minHeight: GROUPED_GEOMETRY.touchRow.minHeightPx, boxSizing: 'border-box' };
}

/**
 * Fixed-width slot for a leading checkbox (month / customer rows). Its width is the 18/20px box
 * (the row's own gap supplies the 12/10px), and the checkbox's larger tap wrapper is pulled back
 * so the VISIBLE box starts at the slot's left edge — the same x on every level.
 */
export function groupedLeadingSlot(isTouch) {
  const s = isTouch ? GROUPED_GEOMETRY.checkSlot.touch : GROUPED_GEOMETRY.checkSlot.web;
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: s.box, height: s.box, overflow: 'visible',
  };
}

/** Padding for the checkbox column cell of a grouped table row (web): box starts at the edge. */
export function groupedCheckColStyle(edge) {
  const w = edge + GROUPED_GEOMETRY.checkSlot.web.box + GROUPED_GEOMETRY.checkSlot.web.gap;
  return {
    width: w, minWidth: w, maxWidth: w, boxSizing: 'border-box', textAlign: 'left',
    padding: `0 0 0 ${edge - 3}px`, verticalAlign: 'middle', lineHeight: 0,
  };
}

/** The ONE trailing column of every grouped row and column header (KAN-954): money ends on its left edge. */
export const GROUPED_TRAILING_PX = 44;

/**
 * Wrapper of a grouped card's records. NO padding and no gap: the shared `mobileCardList` carries a 4px
 * side padding, which pushed every record 4px right of its customer row and column header (and its amount
 * 4px left of the customer total). Records start and end exactly on the group's own edges.
 */
export const groupedCardListStyle = { display: 'flex', flexDirection: 'column', gap: 0, padding: 0 };
